/**
 * Where each numbered box sits on EPA Form 8700-22 and its 8700-22A
 * continuation sheet.
 *
 * A constant, not data. This describes a fixed-layout federal form, so it does
 * not vary by tenant, document or scan, and nothing writes it at runtime. Putting
 * it in the database would buy a migration to seed it, a query to read it, and
 * the ability for it to drift between environments — for a value that changes
 * only when EPA revises the form, which is a code change reviewed like any other.
 *
 * It is deliberately not per-document geometry. Extraction is an LLM over the
 * file buffer, so there are no bounding boxes and no OCR coordinates; getting
 * real ones would need a second pass (Textract `AnalyzeDocument`) at meaningful
 * cost and latency. The form's fixed layout makes that unnecessary for the job
 * this does, which is pointing rather than cropping.
 *
 * **The highlight is a hint.** Coordinates are normalized to the whole page, so a
 * rotated, cropped, skewed or photographed scan lands the outline imprecisely —
 * and those are exactly the documents a reviewer needs help with. It must
 * therefore always be a soft outline the reviewer can look past and pan away
 * from, never a crop and never something the UI treats as authoritative.
 *
 * **Every number here is measured off EPA's own artwork**, both sheets, from the
 * ruled lines rather than from the printed labels — a column header is centred
 * over its cell, so where a label starts is not where its cell does, which is
 * what left the earlier column edges five to ten percent of page width to the
 * left of the cells they named.
 *
 * The procedure, so it can be repeated when EPA revises the form: render the
 * published `8700-22` and `8700-22A` PDFs to greyscale at 150 dpi, take the rows
 * and columns whose dark-pixel count runs the length of the page as the rules,
 * and read the cell edges off them. Vertically that gives a page fraction
 * directly. Horizontally it gives a fraction of the *printed frame*, which is
 * then re-expressed against `FRAME` below — EPA publishes the form on
 * continuous-feed stock whose tractor margins hold the frame well inside the
 * page, while the scans reviewers actually work are trimmed to it.
 *
 * The two sheets' column edges agree within 0.6% of frame width, so they share
 * one set. Bands are authored to meet exactly, which is also the check that the
 * measurement is self-consistent: the main form's four table rows end where Box
 * 14 begins, and the continuation sheet's ten rows end where Box 32 begins.
 *
 * `FormRegionSheet` (staff → Observe → Form Regions) draws the whole map over a
 * document, which is how a correction gets confirmed by eye.
 */

/**
 * A rectangle on a page, normalized 0-1 from the top-left corner.
 *
 * Top-left origin rather than the PDF's own bottom-left: the viewer hands us a
 * page element, so a percentage-positioned overlay needs no coordinate flip and
 * no knowledge of the scan's DPI, and it scales with zoom for free.
 */
export interface BoxRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Which printed form a box number belongs to. */
export type ManifestFormVariant = "8700-22" | "8700-22A";

/** Left and right edges of the printed frame, inside the page margin. */
const FRAME = { left: 0.03, right: 0.97 } as const;
const FRAME_WIDTH = FRAME.right - FRAME.left;

function band(y: number, height: number): BoxRegion {
  return { x: FRAME.left, y, width: FRAME_WIDTH, height };
}

function cell(x: number, y: number, width: number, height: number): BoxRegion {
  return { x, y, width, height };
}

/**
 * The waste-line table on the main form: four rows between the column headers
 * and Box 14. `0.303 + 4 × 0.0455` is Box 14's top edge.
 */
const MAIN_TABLE = { top: 0.303, rowHeight: 0.0455, rows: 4 } as const;

/**
 * The continuation sheet's table: ten rows of the same height as the main form's,
 * starting far higher up the page because the sheet carries none of the
 * certification blocks that sit above the main form's table.
 * `0.226 + 10 × 0.0456` is Box 32's top edge.
 */
const CONTINUATION_TABLE = { top: 0.226, rowHeight: 0.0456, rows: 10 } as const;

/**
 * Column edges of the waste-line table, shared by both sheets — the continuation
 * sheet repeats the main form's columns under different numbers (27-31 for
 * 9a-13).
 */
const COLUMNS = {
  hm: { x: 0.03, width: 0.032 },
  description: { x: 0.062, width: 0.486 },
  containers: { x: 0.548, width: 0.132 },
  quantity: { x: 0.68, width: 0.078 },
  unit: { x: 0.758, width: 0.049 },
  wasteCodes: { x: 0.807, width: 0.163 },
} as const;

/**
 * Which table column a box number addresses, per sheet.
 *
 * Keyed by sheet rather than pooled, because the two number the same columns
 * differently and a pooled map would happily resolve Box 11 on a continuation
 * sheet that has no Box 11 — pointing the reviewer at a column the page does not
 * label that way.
 */
const TABLE_COLUMN: Record<ManifestFormVariant, Record<string, keyof typeof COLUMNS>> = {
  "8700-22": {
    "9a": "hm",
    "9b": "description",
    "10": "containers",
    "11": "quantity",
    "12": "unit",
    "13": "wasteCodes",
  },
  "8700-22A": {
    "27a": "hm",
    "27b": "description",
    "28": "containers",
    "29": "quantity",
    "30": "unit",
    "31": "wasteCodes",
  },
};

/**
 * The management method codes, which repeat per waste line in a grid below the
 * table rather than as a column in it.
 *
 * Box 19 on the main form is four cells across, one per line it carries. Box 36
 * on the continuation sheet is five across and two down, for its ten. The
 * continuation cells carry no printed numbers — unlike Box 19's "1." to "4." —
 * so they take the lines in reading order: across the first row, then the second.
 *
 * Edges are measured rather than divided evenly, because the printed cells are
 * not quite equal: the rightmost is wider on both sheets.
 *
 * Rows are given as the cells' own top and bottom rather than as a run of
 * dividing lines. On the continuation sheet each cell occupies only the lower
 * part of its band — the strip above the first one carries the box's printed
 * label — so treating the bands as the cells would outline that label as though
 * it were where the reviewer is typing.
 */
const MANAGEMENT_METHOD_GRID: Record<
  ManifestFormVariant,
  {
    box: string;
    columnEdges: readonly number[];
    rowBands: readonly (readonly [top: number, bottom: number])[];
  }
> = {
  "8700-22": {
    box: "19",
    columnEdges: [0.03, 0.26, 0.489, 0.719, 0.97],
    rowBands: [[0.879, 0.909]],
  },
  "8700-22A": {
    box: "36",
    columnEdges: [0.03, 0.214, 0.418, 0.602, 0.786, 0.97],
    rowBands: [
      [0.909, 0.924],
      [0.939, 0.954],
    ],
  },
};

/**
 * One waste line's management method cell, or null when this is not that box.
 *
 * Outlining the whole band told the reviewer that the codes are down there,
 * which they could see; it did not say which of the four is the line they are
 * typing into.
 */
function managementMethodCell(
  variant: ManifestFormVariant,
  box: string,
  rowIndex: number
): BoxRegion | null {
  const grid = MANAGEMENT_METHOD_GRID[variant];
  if (grid.box !== box) return null;

  const columns = grid.columnEdges.length - 1;
  // Clamped like the table's rows: pointing at the last cell of the right grid
  // beats pointing off the sheet.
  const cell = Math.min(Math.max(rowIndex, 0), columns * grid.rowBands.length - 1);
  const column = cell % columns;
  const [top, bottom] = grid.rowBands[Math.floor(cell / columns)];

  return {
    x: grid.columnEdges[column],
    y: top,
    width: grid.columnEdges[column + 1] - grid.columnEdges[column],
    height: bottom - top,
  };
}

/**
 * Boxes that are not part of the waste-line table, and so have one fixed place
 * on the sheet.
 */
const FIXED_REGIONS: Record<ManifestFormVariant, Record<string, BoxRegion>> = {
  "8700-22": {
    // Header row. It does not start at the frame: the form's own title block
    // holds the first 15% of it, so Box 1 begins where that ends rather than at
    // the page margin.
    "1": cell(0.172, 0.061, 0.276, 0.031),
    "2": cell(0.448, 0.061, 0.063, 0.031),
    "3": cell(0.511, 0.061, 0.187, 0.031),
    "4": cell(0.698, 0.061, 0.272, 0.031),

    "5": band(0.092, 0.06),
    // Boxes 6 and 7 are one printed row each and the same height; the pair spans
    // the gap between Box 5's content and Box 8's label.
    "6": band(0.152, 0.03),
    "7": band(0.182, 0.03),
    "8": band(0.212, 0.061),

    "14": band(0.485, 0.059),
    "15": band(0.544, 0.077),
    "16": band(0.622, 0.045),
    "17": band(0.667, 0.076),
    // Box 18 covers 18a, 18b and 18c — the whole discrepancy block, which is why
    // it is the tallest band on the sheet.
    "18": band(0.743, 0.122),
    "19": band(0.865, 0.045),
    "20": band(0.909, 0.046),
  },
  "8700-22A": {
    // Header row: generator ID, page number, tracking number. The title block
    // ahead of Box 21 is wider than the main form's, because it carries the
    // "(Continuation Sheet)" line.
    "21": cell(0.275, 0.06, 0.275, 0.031),
    "22": cell(0.55, 0.06, 0.081, 0.031),
    "23": cell(0.631, 0.06, 0.339, 0.031),

    "24": band(0.091, 0.045),
    "25": band(0.136, 0.03),
    "26": band(0.166, 0.029),

    "32": band(0.682, 0.06),
    "33": band(0.742, 0.045),
    "34": band(0.787, 0.046),
    "35": band(0.833, 0.061),
    "36": band(0.894, 0.061),
  },
};

/**
 * Where a box sits on its sheet, or null when the box is not one this map knows.
 *
 * `rowIndex` places a waste-line box in the table — the table's boxes repeat down
 * it, so Box 11 alone does not say which line's quantity is meant. A row past the
 * end of the table clamps to the last row rather than running off the page, since
 * the highlight is a hint and pointing at the bottom of the right table beats
 * pointing at nothing.
 */
export function boxRegion(
  variant: ManifestFormVariant,
  box: string,
  rowIndex?: number
): BoxRegion | null {
  // Before the fixed bands: Box 19/36 has one entry in both, and which the
  // caller wants depends on whether they named a line. The region sheet draws
  // the whole band; a reviewer in a field gets their own cell.
  if (rowIndex != null) {
    const cell = managementMethodCell(variant, box, rowIndex);
    if (cell != null) return cell;
  }

  const fixed = FIXED_REGIONS[variant][box];
  if (fixed) return fixed;

  const column = TABLE_COLUMN[variant][box];
  if (column == null) return null;

  const table = variant === "8700-22" ? MAIN_TABLE : CONTINUATION_TABLE;
  const row = Math.min(Math.max(rowIndex ?? 0, 0), table.rows - 1);
  const { x, width } = COLUMNS[column];

  return { x, y: table.top + row * table.rowHeight, width, height: table.rowHeight };
}

/**
 * Extra height added below a region before it is drawn on a real document.
 *
 * The map is measured off EPA's artwork, and against the scans reviewers
 * actually work the boxes land either on target or a little low — a scan is fed
 * slightly skewed, trimmed a few millimetres short at the top, or photographed
 * at an angle, and all three push the printed form down the page rather than up.
 * The error therefore has a direction, so the slack that absorbs it should too.
 *
 * Expressed as a fraction of page height, not pixels: the overlay is
 * percentage-positioned so it scales with zoom for free. At the width the review
 * pane gives a letter page, this is roughly twenty-five pixels.
 *
 * Deliberately well under half a table row (`0.0455`), so pointing at one waste
 * line's cell reaches toward the line below without reading as though it means
 * that line too.
 *
 * Applied where a region is pointed at, not in `boxRegion` itself: the region
 * sheet draws the map to check it against the artwork, and a map already grown
 * to forgive scan error cannot be checked against anything.
 */
export const REGION_BOTTOM_BUFFER = 0.02;

/** A region grown downward by `REGION_BOTTOM_BUFFER`, clamped to the page. */
export function withBottomBuffer(
  region: BoxRegion,
  buffer: number = REGION_BOTTOM_BUFFER
): BoxRegion {
  const bottom = Math.min(1, region.y + region.height + buffer);
  return { ...region, height: bottom - region.y };
}

/**
 * How much slack to leave around a box when framing it.
 *
 * Two terms, because the error this absorbs does not scale with the box. A
 * cropped, photographed or skewed scan shifts the whole form by some fraction of
 * the *page* — two or three percent of page height is ordinary — and that offset
 * is the same whether the target is a full-width band or a table cell 6% wide.
 * A ratio alone would give the tightest cells, which need slack most, the least
 * of it: 20% of 6% is barely over one percent of the page.
 *
 * So the floor is what protects small boxes and the ratio is what keeps large
 * ones from being framed too tightly.
 */
export const REGION_PADDING = { boxRatio: 0.2, pageFloor: 0.04 } as const;

/**
 * The least page a frame must show, as a fraction of page height.
 *
 * A cap on magnification rather than on the region: framing a 4.7%-tall table
 * cell exactly would put the viewer at roughly sixteen times page width, where an
 * offset of two percent of page height moves the value most of a viewport away.
 * Keeping a quarter of the page in view bounds that to about four times, which
 * leaves a band of context above and below the target for the reader's eye to
 * close the gap with.
 *
 * The consequence is that full-width bands — most of the form — barely zoom at
 * all, and the table cells where the form is dense zoom the most. Which is the
 * right way round.
 */
export const MIN_VISIBLE_PAGE_HEIGHT = 0.25;

/** A region grown by `REGION_PADDING`, clamped to the page. */
export function paddedRegion(
  region: BoxRegion,
  padding: { boxRatio: number; pageFloor: number } = REGION_PADDING
): BoxRegion {
  const padX = Math.max(region.width * padding.boxRatio, padding.pageFloor);
  const padY = Math.max(region.height * padding.boxRatio, padding.pageFloor);

  const left = Math.max(0, region.x - padX);
  const top = Math.max(0, region.y - padY);
  const right = Math.min(1, region.x + region.width + padX);
  const bottom = Math.min(1, region.y + region.height + padY);

  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * What a region needs from the viewer's scale.
 *
 * `fitsAt` is the largest scale at which the whole padded region is still on
 * screen, uncapped. `wanted` is that capped by `MIN_VISIBLE_PAGE_HEIGHT`, which
 * is the scale to actually use. They differ for a small region, where the cap
 * stops the viewer magnifying a table cell to sixteen times page width — and
 * keeping both is what lets a caller tell "already fits" from "already zoomed
 * as far as we allow".
 */
export interface RegionZoom {
  wanted: number;
  fitsAt: number;
}

/**
 * The viewer scale that frames a region, capped so context stays on screen.
 *
 * Page dimensions are the intrinsic ones — what the page measures at scale 1 —
 * because that is the unit the viewer's own scale is expressed in.
 *
 * Returns a scale to zoom *to*, not a decision about whether to. A caller that
 * would be zooming out, or that the reviewer has already zoomed themselves,
 * should leave the viewer alone: following the caret is help, and help that
 * overrides a deliberate action is not help.
 */
export function regionZoomScale({
  region,
  container,
  page,
  minVisiblePageHeight = MIN_VISIBLE_PAGE_HEIGHT,
}: {
  region: BoxRegion;
  /** The scrolling viewport, in pixels. */
  container: { width: number; height: number };
  /** The page at scale 1, in pixels. */
  page: { width: number; height: number };
  minVisiblePageHeight?: number;
}): RegionZoom | null {
  if (container.width <= 0 || container.height <= 0) return null;
  if (page.width <= 0 || page.height <= 0) return null;

  const padded = paddedRegion(region);
  if (padded.width <= 0 || padded.height <= 0) return null;

  // Whichever axis runs out first decides, so the whole padded region fits.
  const fitsAt = Math.min(
    container.width / (page.width * padded.width),
    container.height / (page.height * padded.height)
  );
  const cap = container.height / (page.height * minVisiblePageHeight);

  return { wanted: Math.min(fitsAt, cap), fitsAt };
}

/**
 * Whether to re-frame, and at what scale — or null to leave the viewer alone.
 *
 * Leaving it alone is the common answer, and deliberately so. Following the caret
 * is help; help that throws the pane around while someone tabs sixty fields, or
 * that undoes a closer look they took themselves, is not.
 *
 * `ourLast` is the scale this component set the last time it framed something.
 * Comparing it against the live scale is how a zoom we caused is told apart from
 * one the reviewer caused — the viewer reports both the same way.
 */
export function zoomDecision({
  zoom,
  current,
  ourLast,
}: {
  /** What the region needs, from `regionZoomScale`. */
  zoom: RegionZoom | null;
  /** What the viewer is at now, or null before it has reported a scale. */
  current: number | null;
  /** The scale we set last, or null if we have not set one for this document. */
  ourLast: number | null;
}): number | null {
  if (zoom == null) return null;
  if (current == null) return zoom.wanted;

  // Off the screen entirely: the region is bigger than the viewport at this
  // magnification, so the outline points at something the reviewer cannot see.
  // Pulling back is the one case worth overruling their own zoom for — a frame
  // they cannot look at is not a closer look, it is a lost one.
  if (current > zoom.fitsAt) return zoom.wanted;

  // The reviewer has zoomed since we last did, so from here the magnification is
  // theirs to choose.
  if (ourLast != null && current !== ourLast) return null;

  // It already fits and they are no closer than we put them, so magnifying
  // further is the only thing left worth doing — and only if it gains something.
  if (zoom.wanted <= current) return null;

  return zoom.wanted;
}

/** Every box this map knows for a sheet, for the verification harness. */
export function knownBoxes(variant: ManifestFormVariant): string[] {
  return [...Object.keys(FIXED_REGIONS[variant]), ...Object.keys(TABLE_COLUMN[variant])];
}
